// pages/karigars/AssignWork.jsx
// Every stitch item across all open orders that nobody is working on yet. Pick one
// karigar, tick the items, issue them in a single batch.
//
// The backend resolves each item's rate from karigar_rate_detail and wraps the whole
// batch in one transaction — so a single product the karigar has no rate for fails
// everything and nothing is written. That's why the server's message is shown verbatim
// in a banner instead of a generic toast.
import { useEffect, useMemo, useState } from "react";
import { karigarWorkAPI, karigarsAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { usePagination } from "../../hooks/usePagination";
import { Loading, Pagination, EmptyState, Spinner } from "../../components/ui";
import { fmt, todayISO } from "../../utils/helpers";

const PER_PAGE = 8;

export default function AssignWork() {
  const toast = useToast();

  const [work, setWork]         = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const [search, setSearch]       = useState("");
  const [karigarId, setKarigarId] = useState("");
  const [issueDate, setIssueDate] = useState(todayISO());
  const [selected, setSelected]   = useState([]);

  const load = () => {
    setLoading(true);
    setError("");
    karigarWorkAPI.pendingToAssign()
      .then((d) => {
        setWork(d);
        // Drop ids that are no longer pending, so a stale tick can't be resubmitted.
        setSelected((prev) => prev.filter((id) => d.some((w) => w.osdId === id)));
      })
      .catch((e) => {
        console.error("[AssignWork] load error:", e);
        setWork([]);
        setError(e?.message || "Couldn't load work pending assignment");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    karigarsAPI.list()
      .then(setKarigars)
      .catch((e) => { console.error("[AssignWork] karigar list error:", e); setKarigars([]); });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return work;
    return work.filter((w) =>
      String(w.orderNo ?? "").toLowerCase().includes(q) ||
      w.customerName.toLowerCase().includes(q) ||
      w.forName.toLowerCase().includes(q) ||
      w.product.toLowerCase().includes(q)
    );
  }, [work, search]);

  const { paged, page, setPage, total, count } = usePagination(filtered, PER_PAGE);

  // Select-all spans the whole filtered set, not just the visible page — otherwise
  // "select all" silently means "select all 8 of these".
  const filteredIds  = filtered.map((w) => w.osdId);
  const allSelected  = filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const toggleAll    = () =>
    setSelected(allSelected ? selected.filter((id) => !filteredIds.includes(id))
                            : [...new Set([...selected, ...filteredIds])]);

  const toggleOne = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const karigar = karigars.find((k) => String(k.id) === String(karigarId));
  const canSave = !!karigarId && !!issueDate && selected.length > 0 && !saving;

  const handleAssign = async () => {
    setSaving(true);
    setError("");
    try {
      await karigarWorkAPI.assign({ karigarId, issueDate, osdIds: selected });
      const n = selected.length;
      toast(`${n} item${n > 1 ? "s" : ""} assigned to ${karigar?.name ?? "karigar"} 📤`);
      setSelected([]);
      load();
    } catch (e) {
      console.error("[AssignWork] assign error:", e);
      setError(e?.message || "Failed to assign work");
      toast("Assignment failed — nothing was assigned", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Who + when */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Assign to Karigar <span className="form-required">*</span></label>
            <select
              className="form-input"
              value={karigarId}
              onChange={(e) => setKarigarId(e.target.value)}
              disabled={saving}
            >
              <option value="">Select a karigar…</option>
              {karigars.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}{k.workType ? ` · ${k.workType}` : ""} — ₹{(k.balance || 0).toLocaleString()} due
                </option>
              ))}
            </select>
            {karigar && (
              <span className="td-muted" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                {karigar.phone || "No phone"}{karigar.speciality ? ` · ${karigar.speciality}` : ""}
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Issue Date <span className="form-required">*</span></label>
            <input
              type="date"
              className="form-input"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "var(--red-dim)", border: "1px solid rgba(224,85,85,0.3)",
              borderRadius: "var(--radius-sm)", padding: "12px 14px", marginTop: 4,
              fontSize: 13, color: "var(--red)", lineHeight: 1.6,
            }}
          >
            ❌ {error}
            <div style={{ marginTop: 6, color: "var(--text-muted)" }}>
              Nothing was assigned — the whole batch is rolled back on any failure. A
              missing rate is the usual cause: check this karigar’s Stitch Types &amp;
              Rates under View/Edit.
            </div>
          </div>
        )}
      </div>

      {/* Pending work */}
      <div className="card">
        <div className="card-header">
          <div className="filter-bar" style={{ width: "100%" }}>
            <div className="search-bar" style={{ flex: 1, maxWidth: 320 }}>
              <span className="search-icon">🔍</span>
              <input
                className="form-input"
                placeholder="Search order no, customer or item…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="td-muted" style={{ fontSize: 12.5 }}>
              {count} item{count !== 1 ? "s" : ""} pending
              {selected.length > 0 ? ` · ${selected.length} selected` : ""}
            </span>
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={saving || filtered.length === 0}
                        title="Select all matching items"
                      />
                    </th>
                    <th>Order No</th>
                    <th>Order Date</th>
                    <th>Delivery</th>
                    <th>Customer</th>
                    <th>Item</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          icon="📤"
                          title={search ? "No pending work matches your search" : "Nothing waiting to be assigned"}
                          subtitle={!search ? "Every open stitch item is already out with a karigar." : undefined}
                        />
                      </td>
                    </tr>
                  ) : (
                    paged.map((w) => {
                      const overdue = w.deliveryDate && w.deliveryDate < todayISO();
                      return (
                        <tr key={w.osdId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.includes(w.osdId)}
                              onChange={() => toggleOne(w.osdId)}
                              disabled={saving}
                            />
                          </td>
                          <td style={{ color: "var(--gold)" }}><strong>#{w.orderNo}</strong></td>
                          <td className="td-muted">{fmt(w.orderDate)}</td>
                          <td style={overdue ? { color: "var(--red)", fontWeight: 600 } : undefined}>
                            {fmt(w.deliveryDate)}{overdue ? " ⚠" : ""}
                          </td>
                          <td>
                            {w.customerName || "—"}
                            {/* Only worth the extra line when the garment is for someone else. */}
                            {w.forName && w.forName !== w.customerName && (
                              <div className="td-muted" style={{ fontSize: 12 }}>for {w.forName}</div>
                            )}
                          </td>
                          <td>{w.product || "—"}</td>
                          <td
                            className="td-muted"
                            style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={w.remarks || ""}
                          >
                            {w.remarks || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={total} setPage={setPage} count={count} perPage={PER_PAGE} />
          </>
        )}

        {work.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 16 }}>
            {!karigarId && selected.length > 0 && (
              <span className="td-muted" style={{ fontSize: 12.5 }}>Pick a karigar to continue</span>
            )}
            <button className="btn btn-primary btn-lg" onClick={handleAssign} disabled={!canSave}>
              {saving ? <><Spinner /> Assigning…</> : `📤 Assign Selected (${selected.length})`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
